import React, { ChangeEventHandler } from 'react'

import {
  Badge,
  Group,
  ScrollArea,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { BulkReviewMenu } from '#components/ChangeTracking/BulkReviewMenu'
import { showBulkReviewModal } from '#components/ChangeTracking/Modal'
import { FilterOptions, FilterPicker } from '#components/FilterPicker'
import { Panel } from '#layouts/Script/Panel'
import { ILoadedScript, useMst } from '#state'

import { ChangeTrackingPlaceholder } from './Placeholder'
import { TrackedChangeThreadList } from './TrackedChangeThreadList'

import styles from './Panel.module.css'

export type TrackedChangeFilter = 'open' | 'resolved'
interface Props {
  script: ILoadedScript
  onClose(): void
  isOpen: boolean
}

export function ChangeTrackingPanel(props: Props) {
  if (!props.isOpen) return null

  return <ChangeTrackingSidebar script={props.script} onClose={props.onClose} />
}

const Header = ({
  filter,
  setFilter,
  script,
  includeMenu,
}: {
  filter: FilterOptions
  setFilter: (value: FilterOptions) => void
  script: ILoadedScript
  includeMenu: boolean
}) => (
  <>
    <Group gap={5}>
      <Text fw="bold">Changes</Text>
      <Badge color="orange" size="xs">
        Beta
      </Badge>
    </Group>
    <Group gap={5} style={{ marginLeft: 'auto' }}>
      <FilterPicker filter={filter} setFilter={setFilter} />
      {includeMenu && (
        <BulkReviewMenu
          script={script}
          disabled={
            filter === 'resolved' ||
            script.changeTrackingData.hasNoProposedChanges
          }
        />
      )}
    </Group>
  </>
)

const ChangeTrackingSidebar = observer(function ({
  onClose,
  script,
}: {
  script: ILoadedScript
  onClose(): void
}) {
  const mst = useMst()
  const maxHeight = mst.view.dimensions.scriptScroller.height - 80
  const isEnabled = script.trackingChanges
  const { revisions } = script
  const revisionsActive =
    revisions.tag === 'Resolved' && revisions.revisionsState.enabled
  const [filter, setFilter] = React.useState<FilterOptions>('open')

  const showPlaceholder =
    (filter === 'open' && script.changeTrackingData.hasNoProposedChanges) ||
    (filter === 'resolved' && script.changeTrackingData.hasNoReviewedChanges)

  // the server signals to everyone through CHANGE_TRACKING_ENABLED
  // that they need to reload plugins
  const toggleChangeTracking: ChangeEventHandler<HTMLInputElement> = (e) => {
    if (e.target.checked) {
      script.enableChangeTracking()
      return
    }
    if (script.changeTrackingData.proposedChanges.length > 0) {
      showBulkReviewModal({
        script,
        canReviewTrackedChanges: mst.user.canReviewTrackedChanges,
      })
    } else {
      script.disableChangeTracking()
    }
  }

  return (
    <Panel
      header={
        <Header
          filter={filter}
          setFilter={setFilter}
          script={script}
          includeMenu={mst.user.canReviewTrackedChanges}
        />
      }
      onClose={onClose}
    >
      <ScrollArea.Autosize mah={maxHeight}>
        {showPlaceholder ? (
          <ChangeTrackingPlaceholder
            filter={filter}
            enabled={isEnabled}
            canEnable={mst.user.canEnableChangeTracking}
            isAdmin={mst.user.isCurrentOrgAdmin}
            revisionsActive={revisionsActive}
          />
        ) : (
          <TrackedChangeThreadList script={script} filter={filter} />
        )}
      </ScrollArea.Autosize>
      <Stack className={styles.footer}>
        <Tooltip
          label="Change-tracking can't be activated because Revision Mode has been enabled."
          disabled={!revisionsActive}
        >
          <div>
            <Switch
              label="Change-tracking mode"
              disabled={!mst.user.canEnableChangeTracking || revisionsActive}
              onChange={toggleChangeTracking}
              checked={script.trackingChanges}
              p={10}
              pl={10}
              size="xs"
            />
          </div>
        </Tooltip>
      </Stack>
    </Panel>
  )
})
